import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MasterDataService } from '../../../services/master-data.service';
import { Mode } from '../../../models/mode.model';
import { MaintenanceOrderStatus, MaintenanceOrderStatusCode } from '../../../models/maintenance-order-status.model';
import { Shop } from '../../../models/shop.model';
import { ReplaySubject, forkJoin, take, takeUntil } from 'rxjs';

import { RouterModule } from '@angular/router';

import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-input-date';
import { WorkOrderFilterForm } from '../../../form-model/work-order-filter.form.model';
import { User } from '../../../models/user.model';
import { AuthPolicies } from '../../../auth/auth-policies';
import { utils } from '../../../helper/utils';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';

@Component({
  selector: 'app-advance-filter',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, AemsMultilineTagInputComponent],
  templateUrl: './advance-filter.component.html',
  styleUrl: './advance-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AdvanceFilterComponent implements OnInit {
  @Input({ required: true }) workOrderFilterForm!: FormGroup<WorkOrderFilterForm>;
  @Input() isWorkOrderList: boolean = true;

  modes: Mode[] = [];
  statuses: MaintenanceOrderStatus[] = [];
  shops: Shop[] = [];
  workorderRegex: RegExp = /\b5\d{8}\b/g;
  equipmentNumberRegex: RegExp = /\b[a-zA-Z]{4}\d{6,9}\b/g;
  shopIdRegex: RegExp = /\b[a-zA-Z0-9]{3}\b/g;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private masterDataService: MasterDataService,
    private formBuilder: FormBuilder,
    public authPolicy: AuthPolicies,
  ) {}

  ngOnInit() {
    this.addControlsToTheFormGroup();
    this.getUserAssignedShop();
    this.subscribeToMasterDataApiCalls();
  }

  private addControlsToTheFormGroup() {
    this.workOrderFilterForm.addControl('equipmentId', this.formBuilder.control<string[]>([]));
    this.workOrderFilterForm.addControl('mode', this.formBuilder.control<Mode[] | null>(null));
    this.workOrderFilterForm.addControl('fromDate', this.formBuilder.control<string | null>(null));
    this.workOrderFilterForm.addControl('toDate', this.formBuilder.control<string | null>(null));
    this.workOrderFilterForm.addControl('woNumber', this.formBuilder.control<string[]>([]));
    this.workOrderFilterForm.addControl('status', this.formBuilder.control<MaintenanceOrderStatus[] | null>(null));
    this.workOrderFilterForm.addControl('shop', this.formBuilder.control<string[] | null>(null));
    this.workOrderFilterForm.addControl('showFlaggedWO', this.formBuilder.control<boolean | null>(null));
    this.workOrderFilterForm.addControl('applyWOFilter', this.formBuilder.control<boolean | null>(null));
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  private subscribeToMasterDataApiCalls() {
    forkJoin([this.masterDataService.getRepairModes().pipe(take(1)), this.masterDataService.getMaintenanceOrderStatus()])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([modes, status]) => {
        this.modes = modes;
        this.statuses = status;

        // for approval screen set default filter by role and fetch work orders
        if (!this.isWorkOrderList) {
          this.setFilterStatusforRole();
        }
      });
  }
  private setFilterStatusforRole() {
    let statusDescription: MaintenanceOrderStatus | undefined;
    if (this.authPolicy.managerRole) {
      statusDescription = utils.getMaintenanceOrderStatusText(MaintenanceOrderStatusCode.PendingManagerApproval);
    }
    if (this.authPolicy.specialistRole) {
      statusDescription = utils.getMaintenanceOrderStatusText(MaintenanceOrderStatusCode.PendingSpecialistApproval);
    }
    if (this.authPolicy.canMarkWorkOrderAsTTL) {
      statusDescription = utils.getMaintenanceOrderStatusText(MaintenanceOrderStatusCode.SuspendForTTLCheck);
    }
    if (this.authPolicy.approverRole) {
      statusDescription = utils.getMaintenanceOrderStatusText(MaintenanceOrderStatusCode.PendingApproval);
    }
    const statues = this.statuses.filter((x) => x.description.toLowerCase() === statusDescription?.description.toLowerCase());
    this.workOrderFilterForm.controls['status'].setValue(statues);
    this.workOrderFilterForm.controls.applyWOFilter.setValue(true);
  }
  private getUserAssignedShop() {
    const userData = localStorage.getItem('user');
    if (userData === null) {
      return;
    }
    const user: User = JSON.parse(userData);
    this.shops = user.maintenanceShops;
  }

  applyFilters() {
    this.workOrderFilterForm.controls.applyWOFilter.setValue(true);
  }

  clearFilters() {
    this.workOrderFilterForm.reset();
  }
}
